import React, { useState, useEffect, useCallback } from 'react';
import { ChipSelector } from './ChipSelector';
import { RepeatingBlock } from './RepeatingBlock';
import DayFirstDate from "./DayFirstDate";

// ── Constants ──────────────────────────────────────────────────────
const TOOLS_OPTIONS = [
  { value: 'wisc_v', label: 'WISC-V' }, { value: 'wais_5', label: 'WAIS-5' },
  { value: 'wiat_iii', label: 'WIAT-III' }, { value: 'ktea', label: 'KTEA' },
  { value: 'tills', label: 'TILLS' }, { value: 'nepsy_ii', label: 'NEPSY-II' },
  { value: 'unit', label: 'UNIT' }, { value: 'dkefs', label: 'DKEFS' },
  { value: 'abas3', label: 'ABAS-3 interview' },
  { value: 'basc3_conners', label: 'BASC-3 / Conners interview' },
  { value: 'clinical_interview', label: 'Clinical interview' },
  { value: 'informal', label: 'Informal observation' },
  { value: 'other', label: 'Other' },
];
const ASSESSMENT_TYPE_OPTIONS = [
  { value: 'cognitive', label: 'Cognitive' }, { value: 'autism', label: 'Autism' },
  { value: 'adhd', label: 'ADHD' }, { value: 'fasd', label: 'FASD' },
  { value: 'sld', label: 'Specific Learning Disorder' }, { value: 'other_type', label: 'Other' },
];
const FORMAT_OPTIONS = [
  { value: 'in_person', label: 'In person' }, { value: 'telehealth', label: 'Telehealth' },
  { value: 'school_visit', label: 'School visit' },
];
const PRESENT_OPTIONS = [
  { value: 'client_only', label: 'Client only' }, { value: 'parent', label: 'Parent/carer' },
  { value: 'teacher', label: 'Teacher/aide' }, { value: 'other', label: 'Other' },
];
const ARRIVAL_STATE = [
  { value: 'settled', label: 'Settled' }, { value: 'activated', label: 'Activated/energised' },
  { value: 'anxious', label: 'Anxious or hesitant' }, { value: 'flat', label: 'Flat or withdrawn' },
  { value: 'dysregulated', label: 'Dysregulated' }, { value: 'other', label: 'Other' },
];
const SEPARATION_OPTIONS = [
  { value: 'na', label: 'Not applicable' }, { value: 'easy', label: 'Separated easily' },
  { value: 'warmup', label: 'Needed warm-up' }, { value: 'part_session', label: 'Needed carer to stay part of session' },
  { value: 'throughout', label: 'Needed carer throughout' },
];
const RAPPORT_OPTIONS = [
  { value: 'warm_quickly', label: 'Warm quickly' }, { value: 'gradual', label: 'Gradual warm-up' },
  { value: 'variable', label: 'Variable across session' }, { value: 'guarded', label: 'Remained guarded' },
  { value: 'not_established', label: 'Rapport not established' },
];
const REGULATION_OPTIONS = [
  { value: 'steady', label: 'Steady throughout' }, { value: 'minor', label: 'Minor fluctuations' },
  { value: 'significant', label: 'One or more significant state changes' },
  { value: 'frequent', label: 'Frequently dysregulated' },
];
const REG_STRATEGY_OPTIONS = [
  { value: 'movement', label: 'Movement' }, { value: 'fidgeting', label: 'Fidgeting or object use' },
  { value: 'self_talk', label: 'Verbal self-talk' }, { value: 'breaks', label: 'Seeking breaks' },
  { value: 'adult_support', label: 'Seeking adult support' }, { value: 'withdrawal', label: 'Withdrawal or shutdown' },
  { value: 'humour', label: 'Humour or deflection' }, { value: 'none_needed', label: 'None needed' },
  { value: 'other', label: 'Other' },
];
const ENGAGEMENT_OPTIONS = [
  { value: 'sustained', label: 'Sustained throughout' }, { value: 'strong_early', label: 'Strong early then fading' },
  { value: 'slow_start', label: 'Slow start then building' }, { value: 'fluctuating', label: 'Fluctuating with task type' },
  { value: 'interest_dependent', label: 'Dependent on interest' }, { value: 'minimal', label: 'Minimal engagement' },
];
const VALIDITY_OPTIONS = [
  { value: 'yes', label: 'Yes' }, { value: 'mostly', label: 'Mostly: interpret flagged tasks with care' },
  { value: 'concerns', label: 'Significant validity concerns' },
];
const APPROACH_OPTIONS = [
  { value: 'reflective', label: 'Reflective and careful' }, { value: 'impulsive', label: 'Quick and impulsive' },
  { value: 'perfectionistic', label: 'Perfectionistic' }, { value: 'gives_up', label: 'Gives up quickly when hard' },
  { value: 'persists', label: 'Persists when hard' }, { value: 'guesses', label: 'Guesses freely' },
  { value: 'reluctant_guess', label: 'Reluctant to guess' }, { value: 'checks_work', label: 'Checks own work' },
  { value: 'rushes', label: 'Rushes to finish' },
];
const INSTRUCTION_OPTIONS = [
  { value: 'readily', label: 'Understood readily' }, { value: 'occasional', label: 'Needed occasional repetition' },
  { value: 'frequent', label: 'Needed frequent repetition or rephrasing' },
  { value: 'demo_visual', label: 'Needed demonstration or visual support' },
];
const SELF_MONITORING_OPTIONS = [
  { value: 'sm_frequent', label: 'Frequently self-corrected' }, { value: 'sm_occasional', label: 'Occasionally self-corrected' },
  { value: 'sm_rarely', label: 'Rarely noticed errors' }, { value: 'sm_not_observed', label: 'Not observed' },
];
const QUOTE_RELATES_OPTIONS = [
  { value: 'self_perception', label: 'Self-perception' }, { value: 'effort_fatigue', label: 'Effort and fatigue' },
  { value: 'interests', label: 'Interests' }, { value: 'task_reaction', label: 'Task reaction' },
  { value: 'relationships', label: 'Relationships' }, { value: 'other', label: 'Other' },
];

// Social Communication
const SC_EYE_CONTACT_OPTIONS = [
  { value: 'sc_gaze_integrated', label: 'Typical and integrated' },
  { value: 'sc_gaze_reduced', label: 'Reduced or fleeting' },
  { value: 'sc_gaze_avoided', label: 'Avoided' },
  { value: 'sc_gaze_effortful', label: 'Present but effortful' },
];
const SC_RECIPROCITY_OPTIONS = [
  { value: 'sc_recip_natural', label: 'Flowed naturally' },
  { value: 'sc_recip_scaffolded', label: 'Reciprocal with scaffolding' },
  { value: 'sc_recip_one_sided', label: 'One-sided or parallel' },
  { value: 'sc_recip_limited', label: 'Limited back-and-forth' },
];
const SC_SOCIAL_CUES_OPTIONS = [
  { value: 'sc_cues_understood', label: 'Understood and responded to' },
  { value: 'sc_cues_missed', label: 'Some cues missed or misread' },
  { value: 'sc_cues_inconsistent', label: 'Inconsistently read' },
  { value: 'sc_cues_not_read', label: 'Not consistently read' },
];
const SC_GESTURE_OPTIONS = [
  { value: 'sc_gest_integrated', label: 'Integrated with speech' },
  { value: 'sc_gest_limited', label: 'Limited or absent' },
  { value: 'sc_gest_uncoordinated', label: 'Not coordinated with speech' },
  { value: 'sc_gest_descriptive', label: 'Descriptive gestures used' },
];
const SC_SHARED_ENJOYMENT_OPTIONS = [
  { value: 'sc_enjoy_present', label: 'Present and shared' },
  { value: 'sc_enjoy_one_sided', label: 'Present but one-sided' },
  { value: 'sc_enjoy_limited', label: 'Limited or absent' },
  { value: 'sc_enjoy_interest_only', label: 'Only around own interests' },
];

// Rigidity / Inflexibility (RRB)
const RRB_ROUTINE_OPTIONS = [
  { value: 'rrb_routine_insistence', label: 'Insistence on routine or sameness' },
  { value: 'rrb_routine_preference', label: 'Preference for predictability' },
  { value: 'rrb_routine_distress', label: 'Distress when routine changed' },
  { value: 'rrb_routine_none', label: 'None observed' },
];
const RRB_MOTOR_OPTIONS = [
  { value: 'rrb_motor_hand_finger', label: 'Hand or finger mannerisms' },
  { value: 'rrb_motor_whole_body', label: 'Whole-body movements' },
  { value: 'rrb_motor_subtle', label: 'Subtle repetitive movements' },
  { value: 'rrb_motor_objects', label: 'Repetitive fiddling with objects' },
  { value: 'rrb_motor_none', label: 'None observed' },
];
const RRB_INTERESTS_OPTIONS = [
  { value: 'rrb_int_intense', label: 'Intense or narrow focus' },
  { value: 'rrb_int_steers', label: 'Steers conversation to own interests' },
  { value: 'rrb_int_redirectable', label: 'Returns to interests but redirectable' },
  { value: 'rrb_int_typical', label: 'Range of interests typical' },
];
const RRB_SENSORY_OPTIONS = [
  { value: 'rrb_sens_seeking', label: 'Seeking (touch, movement, mouthing)' },
  { value: 'rrb_sens_avoiding', label: 'Avoiding (sounds, textures, light)' },
  { value: 'rrb_sens_mixed', label: 'Mixed sensory profile' },
  { value: 'rrb_sens_none', label: 'No notable sensory behaviours' },
];
const RRB_TRANSITIONS_OPTIONS = [
  { value: 'rrb_trans_flexible', label: 'Flexible with transitions' },
  { value: 'rrb_trans_difficulty', label: 'Difficulty with transitions' },
  { value: 'rrb_trans_distress', label: 'Distress at transitions' },
  { value: 'rrb_trans_support', label: 'Managed with support' },
];

// ── Styles ──
const sectionStyle = { marginBottom: 32, paddingBottom: 16, borderBottom: '1px solid #e5e7eb' };
const sectionTitle = { fontSize: '1.25rem', fontWeight: 700, color: '#1e3a5f', marginBottom: 4 };
const sectionSub = { fontSize: '0.8125rem', color: '#6b7280', marginBottom: 16 };
const fieldGap = { marginBottom: 16 };
const labelStyle = { fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };
const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  fontSize: '0.875rem', fontFamily: 'inherit', color: '#1a202c',
};
const textareaStyle = { ...inputStyle, minHeight: 80, resize: 'vertical' };

// ── Component ──
export default function UniversalObservationForm({ responses, setResponses }) {
  const r = responses;
  const set = useCallback((key, val) => setResponses(prev => ({ ...prev, [key]: val })), [setResponses]);

  // Repeating blocks stored as JSON strings
  const [stateLog, setStateLog] = useState(() => { try { return JSON.parse(r.state_change_log || '[]'); } catch { return []; } });
  const [quoteBank, setQuoteBank] = useState(() => { try { return JSON.parse(r.quote_bank || '[]'); } catch { return []; } });

  useEffect(() => { setResponses(prev => ({ ...prev, state_change_log: JSON.stringify(stateLog) })); }, [stateLog, setResponses]);
  useEffect(() => { setResponses(prev => ({ ...prev, quote_bank: JSON.stringify(quoteBank) })); }, [quoteBank, setResponses]);

  const tools = r.tools_administered || [];
  const showAcademic = tools.includes('wiat_iii') || tools.includes('tills');
  const showCognitive = tools.includes('wisc_v') || tools.includes('wais_5') || tools.includes('nepsy_ii');
  const showInterview = tools.includes('clinical_interview');
  const carerPresent = (r.present_in_room || []).includes('parent');

  return (
    <div style={{ maxWidth: 800 }}>
      {/* Section 1 - Session Snapshot */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Session Snapshot</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Client initials or code</label>
          <input type="text" style={inputStyle} value={r.client_initials || ''} onChange={e => set('client_initials', e.target.value)} placeholder="e.g. JD" data-testid="client-initials" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Assessment date</label>
          <DayFirstDate label="Date" inputStyle={inputStyle} value={r.assessment_date || ''} onChange={v => set('assessment_date', v)} testId="assessment-date" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Assessment type (select all that apply)" options={ASSESSMENT_TYPE_OPTIONS} value={r.assessment_types || []} onChange={v => set('assessment_types', v)} multi revealField={{ value: 'other_type', placeholder: 'Specify...' }} revealValue={r.assessment_type_other} onRevealChange={v => set('assessment_type_other', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Session number in the battery</label>
          <input type="number" style={inputStyle} value={r.session_number || ''} onChange={e => set('session_number', e.target.value)} placeholder="Optional" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="What was administered this session" options={TOOLS_OPTIONS} value={tools} onChange={v => set('tools_administered', v)} multi revealField={{ value: 'other', placeholder: 'Specify other...' }} revealValue={r.tools_other} onRevealChange={v => set('tools_other', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Format" options={FORMAT_OPTIONS} value={r.format || ''} onChange={v => set('format', v)} />
        </div>
        <div style={{ display: 'flex', gap: 16, ...fieldGap }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Session length (minutes)</label>
            <input type="number" style={inputStyle} value={r.session_length || ''} onChange={e => set('session_length', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Breaks</label>
            <input type="number" style={inputStyle} value={r.breaks ?? '0'} onChange={e => set('breaks', e.target.value)} />
          </div>
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Present" options={PRESENT_OPTIONS} value={r.present_in_room || []} onChange={v => set('present_in_room', v)} multi revealField={{ value: 'other', placeholder: 'Specify who...' }} revealValue={r.present_other} onRevealChange={v => set('present_other', v)} />
        </div>
      </div>

      {/* Section 2 - Arrival, Separation and Rapport */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Arrival, Separation and Rapport</h2>
        <div style={fieldGap}>
          <ChipSelector label="State on arrival" options={ARRIVAL_STATE} value={r.state_on_arrival || ''} onChange={v => set('state_on_arrival', v)} revealField={{ value: 'other', placeholder: 'Describe...' }} revealValue={r.arrival_other} onRevealChange={v => set('arrival_other', v)} />
        </div>
        {carerPresent && (
          <div style={fieldGap}>
            <ChipSelector label="Separation from carer" options={SEPARATION_OPTIONS} value={r.separation || ''} onChange={v => set('separation', v)} />
          </div>
        )}
        <div style={fieldGap}>
          <ChipSelector label="Rapport trajectory" options={RAPPORT_OPTIONS} value={r.rapport || ''} onChange={v => set('rapport', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Notes</label>
          <input type="text" style={inputStyle} value={r.rapport_notes || ''} onChange={e => set('rapport_notes', e.target.value)} placeholder="What helped rapport? Interests, humour, movement, predictability?" />
        </div>
      </div>

      {/* Section 3 - Regulation and State */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Regulation and State Across the Session</h2>
        <div style={fieldGap}>
          <ChipSelector label="Overall regulation" options={REGULATION_OPTIONS} value={r.regulation || ''} onChange={v => set('regulation', v)} />
        </div>
        <div style={fieldGap}>
          <RepeatingBlock
            prompt="Log any significant state changes during the session."
            entries={stateLog} onChange={setStateLog} maxEntries={10}
            addLabel="Add State Change" createEmpty={() => ({ when: '', what_changed: '', trigger: '', recovery: '' })}
            entryTitle={(e) => e.when || 'State change'}
            renderEntry={(entry, idx, update) => (
              <div>
                <div style={fieldGap}><label style={labelStyle}>When</label><input type="text" style={inputStyle} value={entry.when || ''} onChange={e => update({ ...entry, when: e.target.value })} placeholder="e.g. 40 min in, after Block Design" /></div>
                <div style={fieldGap}><label style={labelStyle}>What changed</label><input type="text" style={inputStyle} value={entry.what_changed || ''} onChange={e => update({ ...entry, what_changed: e.target.value })} /></div>
                <div style={fieldGap}><label style={labelStyle}>Likely trigger</label><input type="text" style={inputStyle} value={entry.trigger || ''} onChange={e => update({ ...entry, trigger: e.target.value })} /></div>
                <div style={fieldGap}><label style={labelStyle}>What helped them recover</label><input type="text" style={inputStyle} value={entry.recovery || ''} onChange={e => update({ ...entry, recovery: e.target.value })} /></div>
              </div>
            )}
          />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Regulation strategies observed" options={REG_STRATEGY_OPTIONS} value={r.reg_strategies || []} onChange={v => set('reg_strategies', v)} multi revealField={{ value: 'other', placeholder: 'Describe strategy...' }} revealValue={r.reg_strategies_other} onRevealChange={v => set('reg_strategies_other', v)} />
        </div>
      </div>

      {/* Section 4 - Attention, Engagement and Fatigue */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Attention, Engagement and Fatigue</h2>
        <div style={fieldGap}>
          <ChipSelector label="Engagement pattern" options={ENGAGEMENT_OPTIONS} value={r.engagement || ''} onChange={v => set('engagement', v)} />
        </div>
        <div style={fieldGap}><label style={labelStyle}>Fatigue point</label><input type="text" style={inputStyle} value={r.fatigue_point || ''} onChange={e => set('fatigue_point', e.target.value)} placeholder="When did quality drop, and how did it show?" /></div>
        <div style={fieldGap}><label style={labelStyle}>What supported attention</label><textarea style={textareaStyle} value={r.attention_support || ''} onChange={e => set('attention_support', e.target.value)} placeholder="Breaks, movement, novelty, pacing, encouragement, choice, interest-linking. Be specific: this feeds recommendations." /></div>
        <div style={fieldGap}><label style={labelStyle}>What worked against attention</label><input type="text" style={inputStyle} value={r.attention_hindered || ''} onChange={e => set('attention_hindered', e.target.value)} placeholder="Noise, task length, transitions, abstract material, time of day" /></div>
      </div>

      {/* Section 5 - Effort, Validity and Test-Taking Approach */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Effort, Validity and Test-Taking Approach</h2>
        <div style={fieldGap}>
          <ChipSelector label="Effort and engagement adequate for valid results" options={VALIDITY_OPTIONS} value={r.validity || ''} onChange={v => set('validity', v)} />
        </div>
        {(r.validity === 'mostly' || r.validity === 'concerns') && (
          <div style={fieldGap}><label style={labelStyle}>Which tasks or moments were flagged?</label><input type="text" style={inputStyle} value={r.validity_flagged || ''} onChange={e => set('validity_flagged', e.target.value)} /></div>
        )}
        <div style={fieldGap}>
          <ChipSelector label="Approach to tasks" options={APPROACH_OPTIONS} value={r.approach || []} onChange={v => set('approach', v)} multi />
        </div>
        <div style={fieldGap}><label style={labelStyle}>Response to difficulty and failure</label><textarea style={textareaStyle} value={r.difficulty_response || ''} onChange={e => set('difficulty_response', e.target.value)} placeholder="What happened on hard items? Frustration, avoidance, humour, self-criticism, renewed effort? Their words if possible." /></div>
        <div style={fieldGap}><label style={labelStyle}>Response to praise and encouragement</label><input type="text" style={inputStyle} value={r.praise_response || ''} onChange={e => set('praise_response', e.target.value)} /></div>
      </div>

      {/* Section 6 - Communication */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Communication in Session</h2>
        <div style={fieldGap}>
          <ChipSelector label="Understanding of instructions" options={INSTRUCTION_OPTIONS} value={r.instruction_understanding || ''} onChange={v => set('instruction_understanding', v)} />
        </div>
        <div style={fieldGap}><label style={labelStyle}>Expressive style</label><input type="text" style={inputStyle} value={r.expressive_style || ''} onChange={e => set('expressive_style', e.target.value)} placeholder="Elaborated or minimal, on-topic or tangential, notable vocabulary, articulation, prosody" /></div>
        <div style={fieldGap}><label style={labelStyle}>Anything worth flagging for interpretation</label><input type="text" style={inputStyle} value={r.comm_flag || ''} onChange={e => set('comm_flag', e.target.value)} placeholder="e.g. verbal task scores may underestimate ability given word-finding effort" /></div>
      </div>

      {/* Section 7 - Strategy Use */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Strategy Use and Problem-Solving</h2>
        <div style={fieldGap}><label style={labelStyle}>Strategies observed</label><textarea style={textareaStyle} value={r.strategies || ''} onChange={e => set('strategies', e.target.value)} placeholder="Verbal mediation, chunking, finger counting, visualising, trial and error, asking for clarification, self-correction. Tie to specific tasks where possible." /></div>
        <div style={fieldGap}>
          <ChipSelector label="Self-monitoring" options={SELF_MONITORING_OPTIONS} value={r.self_monitoring || ''} onChange={v => set('self_monitoring', v)} />
        </div>
      </div>

      {/* Section 8 - Social Communication */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Social Communication</h2>
        <p style={sectionSub}>Brief observations of social communication style during this session. Optional: skip if not relevant or captured elsewhere.</p>
        <div style={fieldGap}>
          <ChipSelector label="Eye contact and gaze" options={SC_EYE_CONTACT_OPTIONS} value={r.sc_eye_contact || ''} onChange={v => set('sc_eye_contact', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Conversational reciprocity" options={SC_RECIPROCITY_OPTIONS} value={r.sc_reciprocity || ''} onChange={v => set('sc_reciprocity', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Understanding of social cues" options={SC_SOCIAL_CUES_OPTIONS} value={r.sc_social_cues || ''} onChange={v => set('sc_social_cues', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Use of gesture" options={SC_GESTURE_OPTIONS} value={r.sc_gesture || ''} onChange={v => set('sc_gesture', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Shared enjoyment" options={SC_SHARED_ENJOYMENT_OPTIONS} value={r.sc_shared_enjoyment || ''} onChange={v => set('sc_shared_enjoyment', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Clinician observations</label>
          <textarea style={textareaStyle} value={r.sc_observations || ''} onChange={e => set('sc_observations', e.target.value)} placeholder="Any additional social communication observations worth noting" data-testid="sc-observations" />
        </div>
      </div>

      {/* Section 9 - Rigidity / Inflexibility (RRB) */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Rigidity and Inflexibility</h2>
        <p style={sectionSub}>Brief observations of restricted, repetitive behaviours or inflexibility during this session. Optional: skip if not relevant or captured elsewhere.</p>
        <div style={fieldGap}>
          <ChipSelector label="Routine and sameness" options={RRB_ROUTINE_OPTIONS} value={r.rrb_routine || ''} onChange={v => set('rrb_routine', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Repetitive movements or mannerisms" options={RRB_MOTOR_OPTIONS} value={r.rrb_motor || ''} onChange={v => set('rrb_motor', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Restricted interests" options={RRB_INTERESTS_OPTIONS} value={r.rrb_interests || ''} onChange={v => set('rrb_interests', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Sensory behaviours" options={RRB_SENSORY_OPTIONS} value={r.rrb_sensory || ''} onChange={v => set('rrb_sensory', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Flexibility with transitions" options={RRB_TRANSITIONS_OPTIONS} value={r.rrb_transitions || ''} onChange={v => set('rrb_transitions', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Clinician observations</label>
          <textarea style={textareaStyle} value={r.rrb_observations || ''} onChange={e => set('rrb_observations', e.target.value)} placeholder="Any additional observations about rigidity, repetitive behaviours, or inflexibility" data-testid="rrb-observations" />
        </div>
      </div>

      {/* Section 10 - Quote Bank */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Quote Bank</h2>
        <RepeatingBlock
          prompt="Capture their exact words. Quotes about effort, self-perception, interests, and how tasks felt are gold for reports."
          entries={quoteBank} onChange={setQuoteBank} maxEntries={15}
          addLabel="Add Quote" createEmpty={() => ({ quote: '', context: '', relates_to: [] })}
          entryTitle={(e) => e.quote ? `"${e.quote.substring(0, 50)}${e.quote.length > 50 ? '...' : ''}"` : 'New quote'}
          renderEntry={(entry, idx, update) => (
            <div>
              <div style={fieldGap}><label style={labelStyle}>Quote <span style={{ color: '#ef4444' }}>*</span></label><input type="text" style={inputStyle} value={entry.quote || ''} onChange={e => update({ ...entry, quote: e.target.value })} placeholder="Their exact words" /></div>
              <div style={fieldGap}><label style={labelStyle}>Context</label><input type="text" style={inputStyle} value={entry.context || ''} onChange={e => update({ ...entry, context: e.target.value })} placeholder="What was happening when they said this?" /></div>
              <div style={fieldGap}><ChipSelector label="Relates to" options={QUOTE_RELATES_OPTIONS} value={entry.relates_to || []} onChange={v => update({ ...entry, relates_to: v })} multi /></div>
            </div>
          )}
        />
      </div>

      {/* Section 11 - Domain-Specific (conditional) */}
      {(showAcademic || showCognitive || showInterview) && (
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>Domain-Specific Observations</h2>
          <p style={sectionSub}>Shown based on the tools administered this session.</p>
          {showAcademic && (
            <>
              <div style={fieldGap}><label style={labelStyle}>Reading behaviours</label><textarea style={textareaStyle} value={r.domain_reading || ''} onChange={e => set('domain_reading', e.target.value)} placeholder="Decoding approach, fluency, self-corrections, comprehension strategies, avoidance" /></div>
              <div style={fieldGap}><label style={labelStyle}>Writing behaviours</label><textarea style={textareaStyle} value={r.domain_writing || ''} onChange={e => set('domain_writing', e.target.value)} placeholder="Pencil grip, letter formation, planning, spelling attempts, output vs verbal ability" /></div>
              <div style={fieldGap}><label style={labelStyle}>Maths behaviours</label><textarea style={textareaStyle} value={r.domain_maths || ''} onChange={e => set('domain_maths', e.target.value)} placeholder="Counting strategies, fact retrieval, working memory load signs, anxiety" /></div>
            </>
          )}
          {showCognitive && (
            <div style={fieldGap}><label style={labelStyle}>Cognitive task behaviours</label><textarea style={textareaStyle} value={r.domain_cognitive || ''} onChange={e => set('domain_cognitive', e.target.value)} placeholder="Subtest-specific observations worth carrying into interpretation: e.g. approach on Block Design, strategy on Digit Span, speed-accuracy trade-offs" /></div>
          )}
          {showInterview && (
            <div style={fieldGap}><label style={labelStyle}>Interview observations</label><textarea style={textareaStyle} value={r.domain_interview || ''} onChange={e => set('domain_interview', e.target.value)} placeholder="Insight, reflection, topic navigation, emotional expression during interview" /></div>
          )}
          <div style={fieldGap}><label style={labelStyle}>Anything else notable</label><textarea style={textareaStyle} value={r.domain_other || ''} onChange={e => set('domain_other', e.target.value)} /></div>
        </div>
      )}

      {/* Section 12 - Incongruencies */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Incongruencies and Notable Moments</h2>
        <div style={fieldGap}><label style={labelStyle}>Mismatches</label><textarea style={textareaStyle} value={r.mismatches || ''} onChange={e => set('mismatches', e.target.value)} placeholder="Performance vs presentation, in-session vs reported functioning, one task vs another. What did not fit the expected picture?" /></div>
        <div style={fieldGap}><label style={labelStyle}>Off-task and transition moments</label><textarea style={textareaStyle} value={r.off_task || ''} onChange={e => set('off_task', e.target.value)} placeholder="Waiting room, breaks, packing up, chat between subtests" /></div>
      </div>

      {/* General Clinical Observations */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>General Clinical Observations</h2>
        <div style={fieldGap}><label style={labelStyle}>General clinical observations</label><textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.general_clinical_observations || ''} onChange={e => set('general_clinical_observations', e.target.value)} /></div>
        <div style={fieldGap}><label style={labelStyle}>General client quotes</label><textarea style={{ ...textareaStyle, minHeight: 80 }} value={r.general_client_quotes || ''} onChange={e => set('general_client_quotes', e.target.value)} /></div>
      </div>

      {/* Section 13 - Synthesis */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={sectionTitle}>Synthesis</h2>
        <div style={fieldGap}><label style={labelStyle}>Key observational themes <span style={{ color: '#ef4444' }}>*</span></label><textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.synthesis_themes || ''} onChange={e => set('synthesis_themes', e.target.value)} placeholder="3 to 5 sentences capturing the session" /></div>
        <div style={fieldGap}><label style={labelStyle}>Strengths and what helped <span style={{ color: '#ef4444' }}>*</span></label><textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.synthesis_strengths || ''} onChange={e => set('synthesis_strengths', e.target.value)} placeholder="Skills and interests observed, plus the specific conditions under which this person did well" /></div>
        <div style={fieldGap}><label style={labelStyle}>Interpretation and follow-up notes</label><textarea style={{ ...textareaStyle, minHeight: 80 }} value={r.synthesis_notes || ''} onChange={e => set('synthesis_notes', e.target.value)} placeholder="Caveats for score interpretation, hypotheses to test, what to ask parents or school, adjustments for next session" /></div>
      </div>
    </div>
  );
}
